<template>
  <div class="ivr-voicemails-layout">
    <Sidebar />
    <div class="main-content">
      <TopHeader pageTitle="IVR Voicemails" />
      <main class="page-content">

        <!-- Page Header -->
        <div class="page-header">
          <div class="header-content">
            <h1>
              Voicemails
              <span v-if="unreadCount > 0" class="unread-badge">{{ unreadCount }} unread</span>
            </h1>
            <p class="subtitle">Incoming voicemails from your IVR flows.</p>
          </div>
          <button
            class="filter-toggle"
            :class="{ active: showUnreadOnly }"
            @click="toggleUnreadFilter"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M12 3c-4.97 0-9 4.03-9 9s4.03 9 9 9 9-4.03 9-9c0-.46-.04-.92-.1-1.36-.98 1.37-2.58 2.26-4.4 2.26-2.98 0-5.4-2.42-5.4-5.4 0-1.81.89-3.42 2.26-4.4-.44-.06-.9-.1-1.36-.1z"/></svg>
            {{ showUnreadOnly ? 'Show All' : 'Unread Only' }}
          </button>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="loading-state">
          <div class="spinner"></div>
        </div>
        <div v-else-if="error" class="error-state">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
          {{ error }}
        </div>

        <!-- Voicemail List -->
        <div v-else class="vm-list">
          <div
            v-for="vm in voicemails"
            :key="vm.id"
            class="vm-card"
            :class="{ unread: !vm.is_read }"
            @click="openVoicemail(vm)"
          >
            <div class="vm-card-left">
              <div class="vm-dot" :class="{ unread: !vm.is_read }"></div>
              <div class="vm-info">
                <div class="vm-caller">{{ vm.caller_id }}</div>
                <div class="vm-transcription">{{ vm.transcription || 'No transcription available' }}</div>
              </div>
            </div>
            <div class="vm-card-right">
              <span class="vm-duration" v-if="vm.duration_sec">{{ vm.duration_sec }}s</span>
              <span class="vm-date">{{ formatDate(vm.created_at) }}</span>
              <button class="delete-icon-btn" @click.stop="confirmDelete(vm)" title="Delete">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg>
              </button>
            </div>
          </div>

          <!-- Empty State -->
          <div v-if="!voicemails.length" class="empty-state">
            <div class="empty-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor"><path d="M12 3c-4.97 0-9 4.03-9 9s4.03 9 9 9 9-4.03 9-9c0-.46-.04-.92-.1-1.36-.98 1.37-2.58 2.26-4.4 2.26-2.98 0-5.4-2.42-5.4-5.4 0-1.81.89-3.42 2.26-4.4-.44-.06-.9-.1-1.36-.1z"/></svg>
            </div>
            <h3>{{ showUnreadOnly ? 'No unread voicemails' : 'No voicemails yet' }}</h3>
            <p>{{ showUnreadOnly ? 'You\'re all caught up!' : 'Voicemails left by callers will appear here.' }}</p>
          </div>
        </div>

        <!-- Voicemail Player Modal -->
        <div v-if="selectedVM" class="modal-overlay" @click.self="selectedVM = null">
          <div class="modal-container modal-player">
            <div class="modal-header">
              <div>
                <div class="player-caller">{{ selectedVM.caller_id }}</div>
                <div class="player-meta">{{ formatDate(selectedVM.created_at) }}<span v-if="selectedVM.duration_sec"> · {{ selectedVM.duration_sec }}s</span></div>
              </div>
              <button class="modal-close" @click="selectedVM = null">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
              </button>
            </div>
            <div class="modal-body">
              <div v-if="vmAudioLoading" class="loading-state" style="padding: 40px 0;">
                <div class="spinner"></div>
              </div>
              <div v-else>
                <audio v-if="vmAudioUrl" :src="vmAudioUrl" controls style="width:100%; margin-bottom: 20px;"></audio>
                <div class="transcription-box">
                  <div class="transcription-label">Transcription</div>
                  <div class="transcription-text">{{ selectedVM.transcription || 'No transcription available.' }}</div>
                </div>
              </div>
            </div>
            <div class="modal-footer">
              <button class="btn-danger" @click="confirmDelete(selectedVM)">Delete</button>
              <button class="btn-secondary" @click="selectedVM = null">Close</button>
            </div>
          </div>
        </div>

        <!-- Delete Confirm Modal -->
        <div v-if="deleteTarget" class="modal-overlay" @click.self="deleteTarget = null">
          <div class="modal-container modal-sm">
            <div class="modal-header">
              <h2>Delete Voicemail</h2>
              <button class="modal-close" @click="deleteTarget = null">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
              </button>
            </div>
            <div class="modal-body">
              <p class="confirm-text">Delete this voicemail from <strong>{{ deleteTarget.caller_id }}</strong>? This cannot be undone.</p>
            </div>
            <div class="modal-footer">
              <button class="btn-secondary" @click="deleteTarget = null">Cancel</button>
              <button class="btn-danger" :disabled="deleting" @click="doDelete">{{ deleting ? 'Deleting...' : 'Delete' }}</button>
            </div>
          </div>
        </div>

      </main>
    </div>
  </div>
</template>

<script>
import Sidebar from '@/components/Sidebar.vue'
import TopHeader from '@/components/TopHeader.vue'
import { IVRService } from '@/services/ivrService'

export default {
  name: 'IVRVoicemailsView',
  components: { Sidebar, TopHeader },
  data () {
    return {
      voicemails: [],
      loading: true,
      error: null,
      unreadCount: 0,
      showUnreadOnly: false,
      selectedVM: null,
      vmAudioUrl: null,
      vmAudioLoading: false,
      deleteTarget: null,
      deleting: false
    }
  },
  async mounted () { await this.load() },
  methods: {
    async load () {
      this.loading = true
      this.error = null
      try {
        const params = {}
        if (this.showUnreadOnly) params.unread = 1
        const res = await IVRService.getVoicemails(params)
        this.voicemails = res.voicemails || []
        this.unreadCount = this.voicemails.filter(v => !v.is_read).length
      } catch (e) {
        this.error = e.message
      } finally {
        this.loading = false
      }
    },

    async toggleUnreadFilter () {
      this.showUnreadOnly = !this.showUnreadOnly
      await this.load()
    },

    async openVoicemail (vm) {
      this.selectedVM = vm
      this.vmAudioUrl = null
      this.vmAudioLoading = true
      try {
        const res = await IVRService.getVoicemail(vm.id)
        this.selectedVM = res.voicemail
        this.vmAudioUrl = res.audio_url
        if (!vm.is_read) {
          await IVRService.markVoicemailRead(vm.id)
          const idx = this.voicemails.findIndex(v => v.id === vm.id)
          if (idx >= 0) {
            this.voicemails[idx] = { ...this.voicemails[idx], is_read: 1 }
            this.unreadCount = Math.max(0, this.unreadCount - 1)
          }
        }
      } catch (e) {
        // keep existing data
      } finally {
        this.vmAudioLoading = false
      }
    },

    confirmDelete (vm) {
      this.deleteTarget = vm
    },

    async doDelete () {
      this.deleting = true
      try {
        await IVRService.deleteVoicemail(this.deleteTarget.id)
        if (this.selectedVM?.id === this.deleteTarget.id) this.selectedVM = null
        this.deleteTarget = null
        await this.load()
      } catch (e) {
        alert('Error: ' + e.message)
      } finally {
        this.deleting = false
      }
    },

    formatDate (str) {
      if (!str) return '—'
      return new Date(str).toLocaleString()
    }
  }
}
</script>

<style scoped>
.ivr-voicemails-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-secondary);
}
.main-content {
  flex: 1;
  margin-left: 280px;
  display: flex;
  flex-direction: column;
}
.page-content {
  flex: 1;
  padding: 24px 32px;
  overflow-y: auto;
}

/* Page Header */
.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 28px;
}
.header-content h1 {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 4px;
  display: flex;
  align-items: center;
  gap: 12px;
}
.subtitle {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0;
}

.unread-badge {
  background: #3B82F6;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 20px;
}

.filter-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  background: var(--bg-primary);
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-secondary);
  transition: all 0.2s;
  white-space: nowrap;
}
.filter-toggle:hover { background: var(--bg-tertiary); color: var(--text-primary); }
.filter-toggle.active {
  background: rgba(59,130,246,0.08);
  color: #3B82F6;
  border-color: rgba(59,130,246,0.3);
}

/* Loading / Error / Empty */
.loading-state {
  display: flex;
  justify-content: center;
  padding: 80px 0;
}
.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid var(--border-primary);
  border-top-color: #3B82F6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

.error-state {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #FEF2F2;
  border: 1px solid #FECACA;
  border-radius: 12px;
  padding: 16px 20px;
  color: #DC2626;
  font-size: 14px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 80px 20px;
  color: var(--text-secondary);
  text-align: center;
}
.empty-icon {
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.5;
}
.empty-state h3 { font-size: 18px; font-weight: 600; color: var(--text-primary); margin: 0; }
.empty-state p { font-size: 14px; margin: 0; }

/* Voicemail List */
.vm-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.vm-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  padding: 16px 20px;
  cursor: pointer;
  transition: all 0.2s;
  gap: 16px;
}
.vm-card:hover {
  border-color: var(--border-secondary);
  box-shadow: 0 4px 12px rgba(0,0,0,0.06);
  background: var(--bg-primary);
}
.vm-card.unread {
  border-left: 3px solid #3B82F6;
}

.vm-card-left {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  flex: 1;
  min-width: 0;
}
.vm-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: var(--border-primary);
  flex-shrink: 0;
  margin-top: 5px;
}
.vm-dot.unread { background: #3B82F6; }
.vm-info { flex: 1; min-width: 0; }
.vm-caller {
  font-size: 14px;
  font-weight: 700;
  font-family: monospace;
  color: var(--text-primary);
  margin-bottom: 3px;
}
.vm-transcription {
  font-size: 13px;
  color: var(--text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 500px;
}

.vm-card-right {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-shrink: 0;
}
.vm-duration {
  font-size: 12px;
  color: var(--text-secondary);
  background: var(--bg-tertiary);
  padding: 3px 8px;
  border-radius: 6px;
  border: 1px solid var(--border-primary);
}
.vm-date { font-size: 12px; color: var(--text-secondary); white-space: nowrap; }

.delete-icon-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid var(--border-primary);
  background: var(--bg-primary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #EF4444;
  transition: all 0.2s;
  flex-shrink: 0;
}
.delete-icon-btn:hover { background: #FEF2F2; border-color: #FECACA; }

/* Buttons */
.btn-primary {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #3B82F6;
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;
}
.btn-primary:hover:not(:disabled) { background: #2563EB; transform: translateY(-1px); }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }

.btn-secondary {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border: 1px solid var(--border-primary);
  background: var(--bg-primary);
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
  transition: all 0.2s;
}
.btn-secondary:hover { background: var(--bg-tertiary); }

.btn-danger {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #EF4444;
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;
}
.btn-danger:hover:not(:disabled) { background: #DC2626; }
.btn-danger:disabled { opacity: 0.6; cursor: not-allowed; }

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}
.modal-container {
  background: var(--bg-primary);
  border-radius: 16px;
  width: 100%;
  max-width: 520px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.3);
}
.modal-player { max-width: 560px; }
.modal-sm { max-width: 400px; }
.modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 24px 24px 0;
}
.modal-header h2 {
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
}
.modal-close {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid var(--border-primary);
  background: var(--bg-secondary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  transition: all 0.2s;
  flex-shrink: 0;
}
.modal-close:hover { background: var(--bg-tertiary); color: var(--text-primary); }
.modal-body { padding: 20px 24px; }
.modal-footer {
  padding: 0 24px 24px;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

.player-caller {
  font-size: 18px;
  font-weight: 700;
  font-family: monospace;
  color: var(--text-primary);
}
.player-meta {
  font-size: 13px;
  color: var(--text-secondary);
  margin-top: 4px;
}
.transcription-box {
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  padding: 16px;
}
.transcription-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-secondary);
  margin-bottom: 10px;
}
.transcription-text {
  font-size: 14px;
  color: var(--text-primary);
  line-height: 1.7;
}

.confirm-text {
  font-size: 15px;
  color: var(--text-primary);
  margin: 0;
  line-height: 1.5;
}
</style>
